import { useEffect, useState } from "react";
import api, { API } from "../../lib/api";
import { Loading, PageHeader } from "../../components/shared";
import { Button } from "../../components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import { toast } from "sonner";
import { Download, Send, ShieldCheck } from "lucide-react";

export default function AdminExpert360() {
  const [experts, setExperts] = useState(null);
  const [detail, setDetail] = useState(null);
  const load = () =>
    api.get("/admin/experts").then((response) => setExperts(response.data));
  useEffect(() => {
    load();
  }, []);
  const view = async (expertId) => {
    try {
      setDetail((await api.get(`/admin/experts-portal/${expertId}/360`)).data);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not load Expert 360");
    }
  };
  const verify = async (status) => {
    await api.patch(
      `/admin/experts-portal/${detail.expert.expert_id}/verification`,
      { status },
    );
    toast.success("Verification updated");
    view(detail.expert.expert_id);
    load();
  };
  const account = async (account_status) => {
    await api.patch(
      `/admin/experts-portal/${detail.expert.expert_id}/account`,
      { account_status },
    );
    toast.success("Account status updated");
    view(detail.expert.expert_id);
    load();
  };
  const review = async (credential, review_status) => {
    const reason =
      review_status === "REJECTED"
        ? window.prompt("Safe correction reason") || ""
        : "";
    if (review_status === "REJECTED" && !reason) return;
    await api.patch(
      `/admin/experts-portal/credentials/${credential.credential_id}`,
      { review_status, reason },
    );
    view(detail.expert.expert_id);
  };
  if (!experts) return <Loading />;
  return (
    <div>
      <PageHeader
        title="Experts"
        subtitle="Expert identity, verification, private credentials, and account controls."
      />
      <div className="space-y-3">
        {experts.map((expert) => (
          <article
            className="flex flex-wrap items-center justify-between gap-4 border bg-card p-4"
            data-testid={`admin-expert-${expert.expert_id}`}
            key={expert.expert_id}
          >
            <div>
              <p className="font-display font-bold">{expert.name}</p>
              <p className="text-sm text-muted-foreground">
                {expert.email} · {expert.profession || "Professional"}
              </p>
              <p className="mt-1 text-xs">
                {expert.professional_verification_status || expert.verification}{" "}
                · {expert.account_status || "ACTIVE"}
              </p>
            </div>
            <Button
              data-testid={`admin-expert-360-${expert.expert_id}`}
              onClick={() => view(expert.expert_id)}
              type="button"
            >
              Review 360
            </Button>
          </article>
        ))}
      </div>
      <Dialog open={!!detail} onOpenChange={(open) => !open && setDetail(null)}>
        <DialogContent
          className="max-h-[85vh] max-w-3xl overflow-y-auto"
          data-testid="expert-360-dialog"
        >
          <DialogHeader>
            <DialogTitle>{detail?.expert.name} — Expert 360</DialogTitle>
          </DialogHeader>
          {detail && (
            <div className="space-y-5 text-sm">
              <section
                className="grid gap-3 border-y py-4 sm:grid-cols-3"
                data-testid="expert-360-statuses"
              >
                <Item
                  label="Expert ID"
                  value={detail.expert.expert_display_id}
                />
                <Item
                  label="Application"
                  value={detail.expert.application_status}
                />
                <Item
                  label="Verification"
                  value={detail.expert.professional_verification_status}
                />
                <Item label="Account" value={detail.expert.account_status} />
                <Item
                  label="Completion"
                  value={`${detail.expert.profile_completion.percentage}%`}
                />
                <Item
                  label="Location"
                  value={[detail.expert.city, detail.expert.state]
                    .filter(Boolean)
                    .join(", ")}
                />
              </section>
              <div className="flex flex-wrap gap-2">
                <Button
                  data-testid="expert-360-verify"
                  onClick={() => verify("VERIFIED")}
                  type="button"
                >
                  <ShieldCheck className="mr-1 h-4 w-4" />
                  Verify
                </Button>
                <Button
                  data-testid="expert-360-docs-pending"
                  onClick={() => verify("UNDER_REVIEW")}
                  type="button"
                  variant="outline"
                >
                  Under review
                </Button>
                {detail.expert.account_status !== "ACTIVE" && (
                  <Button
                    data-testid="expert-360-resend"
                    onClick={() =>
                      api
                        .post(
                          `/admin/experts-portal/${detail.expert.expert_id}/activation`,
                        )
                        .then(() => toast.success("Activation processed"))
                    }
                    type="button"
                    variant="outline"
                  >
                    <Send className="mr-1 h-4 w-4" />
                    Resend activation
                  </Button>
                )}
                <Button
                  data-testid="expert-360-account"
                  onClick={() =>
                    account(
                      detail.expert.account_status === "SUSPENDED"
                        ? "ACTIVE"
                        : "SUSPENDED",
                    )
                  }
                  type="button"
                  variant="outline"
                >
                  {detail.expert.account_status === "SUSPENDED"
                    ? "Reactivate"
                    : "Suspend"}
                </Button>
              </div>
              <section>
                <h3 className="font-semibold">Expertise</h3>
                <p>
                  {detail.expertise.map((row) => row.service.name).join(", ") ||
                    "None selected"}
                </p>
              </section>
              <section>
                <h3 className="font-semibold">Availability</h3>
                <p>
                  {detail.availability
                    .map(
                      (row) =>
                        `${row.day_of_week}: ${row.start}–${row.end} ${row.timezone}`,
                    )
                    .join(" · ") || "Not configured"}
                </p>
              </section>
              <section>
                <h3 className="font-semibold">Consultation foundation</h3>
                <p>
                  {detail.consultation_settings.enabled
                    ? "Enabled"
                    : "Disabled"}{" "}
                  ·{" "}
                  {(detail.consultation_settings.modes || []).join(", ") ||
                    "No modes"}
                </p>
              </section>
              <section data-testid="expert-360-operations">
                <h3 className="font-semibold">Pilot Operations</h3>
                <div className="mt-2 grid gap-2 sm:grid-cols-3">
                  <Item
                    label="Requests"
                    value={
                      detail.operational_summary?.summary?.new_requests || 0
                    }
                  />
                  <Item
                    label="Appointments"
                    value={
                      detail.operational_summary?.summary
                        ?.upcoming_appointments || 0
                    }
                  />
                  <Item
                    label="Completed sessions"
                    value={
                      detail.operational_summary?.summary
                        ?.completed_consultations || 0
                    }
                  />
                  <Item
                    label="Commercial records"
                    value={
                      detail.operational_summary?.summary?.commercial_records ||
                      0
                    }
                  />
                  <Item
                    label="Calculated value"
                    value={`₹${((detail.operational_summary?.summary?.calculated_consultation_value_paise || 0) / 100).toFixed(2)}`}
                  />
                  <Item
                    label="Calculated entitlement"
                    value={`₹${((detail.operational_summary?.summary?.calculated_expert_entitlement_paise || 0) / 100).toFixed(2)}`}
                  />
                </div>
              </section>
              <section>
                <h3 className="font-semibold">Private credentials</h3>
                <div className="mt-2 space-y-2">
                  {detail.credentials.map((credential) => (
                    <div
                      className="flex flex-wrap items-center justify-between gap-2 border p-3"
                      data-testid={`expert-360-credential-${credential.credential_id}`}
                      key={credential.credential_id}
                    >
                      <span>
                        {credential.credential_type} ·{" "}
                        {credential.review_status}
                      </span>
                      <div className="flex gap-2">
                        <a
                          data-testid={`expert-credential-download-${credential.credential_id}`}
                          href={`${API}/admin/experts-portal/credentials/${credential.credential_id}/download`}
                        >
                          <Download className="h-4 w-4" />
                        </a>
                        <Button
                          data-testid={`expert-credential-verify-${credential.credential_id}`}
                          onClick={() => review(credential, "VERIFIED")}
                          size="sm"
                          type="button"
                        >
                          Verify
                        </Button>
                        <Button
                          data-testid={`expert-credential-reject-${credential.credential_id}`}
                          onClick={() => review(credential, "REJECTED")}
                          size="sm"
                          type="button"
                          variant="outline"
                        >
                          Reject
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
function Item({ label, value }) {
  return (
    <div>
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className="font-semibold">
        {String(value || "Not available").replaceAll("_", " ")}
      </p>
    </div>
  );
}
